import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Lightbulb, Sparkles, ArrowRight, ExternalLink, Play, Layers } from "lucide-react";
import { usePageContext } from "@/hooks/use-page-context";
import { askSpotter } from "@/components/ai-agent";
import { cn } from "@/lib/utils";
import { SectionHeading, HairlineCard, TipList, LinkRowButton } from "./tips-primitives";
import { TipsPanelHeader } from "./TipsPanelHeader";
import { AskSpotterSection } from "./AskSpotterSection";

export function TipsPanel() {
  const navigate = useNavigate();
  const {
    tips,
    hasTips,
    section,
    isTipsPanelOpen,
    closeTipsPanel,
    spotterSuggestions,
    activeSectionTips,
  } = usePageContext();

  const shouldShow = hasTips || activeSectionTips;
  if (!shouldShow) return null;

  const isShowingSectionTips = !!activeSectionTips;
  const displayIcon = isShowingSectionTips ? activeSectionTips.icon : tips?.icon;
  const Icon = displayIcon!;

  const handleSpotterQuestion = (question: string) => {
    closeTipsPanel();
    askSpotter(question);
  };

  const handleNavigate = (path: string) => {
    closeTipsPanel();
    navigate(path);
  };

  const renderSectionTips = () =>
    activeSectionTips && (
      <>
        <TipsPanelHeader
          icon={Icon}
          title={activeSectionTips.title}
          description={activeSectionTips.description}
          badge="Section guide"
        />

        <ScrollArea className="flex-1">
          <div className="px-5 py-5 space-y-6">
            <div className="space-y-2">
              <SectionHeading icon={Sparkles}>Why this matters</SectionHeading>
              <p className="font-sans text-[13px] leading-[1.55] text-muted-foreground">
                {activeSectionTips.valueProp}
              </p>
            </div>

            <div className="space-y-2.5">
              <SectionHeading icon={Layers}>Key features</SectionHeading>
              <div className="flex flex-col gap-1.5">
                {activeSectionTips.keyFeatures.map((feature, index) => (
                  <HairlineCard key={index}>
                    <div className="font-sans text-[13px] font-medium text-foreground">
                      {feature.title}
                    </div>
                    <div className="mt-0.5 font-sans text-[12px] text-muted-foreground">
                      {feature.description}
                    </div>
                  </HairlineCard>
                ))}
              </div>
            </div>

            <div className="space-y-2.5">
              <SectionHeading icon={Lightbulb}>Pro tips</SectionHeading>
              <TipList items={activeSectionTips.tips} />
            </div>

            <AskSpotterSection
              suggestions={spotterSuggestions}
              onAsk={handleSpotterQuestion}
            />

            {activeSectionTips.getStartedPath && (
              <Button
                className={cn(
                  "w-full bg-primary text-primary-foreground",
                  "hover:bg-primary/90 hover:volt-glow-sm",
                  "transition-all duration-150 ease-out"
                )}
                onClick={() => handleNavigate(activeSectionTips.getStartedPath!)}
              >
                Get started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </ScrollArea>
      </>
    );

  const renderPageTips = () =>
    tips && (
      <>
        <TipsPanelHeader
          icon={Icon}
          title={tips.title}
          description={tips.description}
          badge={section || "Tips"}
        />

        <ScrollArea className="flex-1">
          <div className="px-5 py-5 space-y-6">
            <div className="space-y-2">
              <SectionHeading icon={Sparkles}>Why this matters</SectionHeading>
              <p className="font-sans text-[13px] leading-[1.55] text-muted-foreground">
                {tips.valueProp}
              </p>
            </div>

            <div className="space-y-2.5">
              <SectionHeading icon={Lightbulb}>Quick tips</SectionHeading>
              <TipList items={tips.tips} />
            </div>

            {tips.videoUrl && (
              <div className="space-y-2.5">
                <SectionHeading icon={Play}>Video tutorial</SectionHeading>
                <LinkRowButton
                  onClick={() => window.open(tips.videoUrl, "_blank")}
                  trailing={
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  }
                >
                  Watch the tutorial
                </LinkRowButton>
              </div>
            )}

            <AskSpotterSection
              suggestions={spotterSuggestions}
              onAsk={handleSpotterQuestion}
            />

            {tips.relatedPages && tips.relatedPages.length > 0 && (
              <div className="space-y-2.5">
                <SectionHeading icon={ArrowRight}>Related features</SectionHeading>
                <div className="flex flex-wrap gap-1.5">
                  {tips.relatedPages.map((page, index) => (
                    <button
                      key={index}
                      onClick={() => handleNavigate(page.path)}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 font-sans text-[12px] text-foreground transition-colors duration-150 ease-out hover:bg-muted"
                    >
                      {page.label}
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </>
    );

  return (
    <Sheet open={isTipsPanelOpen} onOpenChange={closeTipsPanel}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col bg-background border-l border-border"
      >
        {isShowingSectionTips ? renderSectionTips() : renderPageTips()}

        <div className="border-t border-border bg-card px-4 py-3">
          <p className="text-center font-mono text-[11px] text-muted-foreground">
            Press{" "}
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
              ?
            </kbd>{" "}
            anytime to open tips
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
